// import { set } from 'vue'
import axios from 'axios';

const INITIAL_STATE = {
  signupArray: [],
  signups: { CG: null, DSN: null, PM: null, MD: null },
  invitedMcrSignups: {},
  pending_signups: [
    {
      type: 'CG',
      name: 'Connect Group Experience',
    },
    {
      type: 'DSN',
      name: 'Dreams & Stories Night',
    },
    {
      type: 'PM',
      name: 'Prayer Meeting Experience',
    },
    {
      type: 'MD',
      name: 'Ministry Debrief',
    },
    {
      type: 'MDD',
      name: 'Ministry Deep Dive',
    },
  ],
  SIGN_UP_CLOSE_TS: '',
  EXP_EVENT_END_TS: '',
  EXP_ALLOC_TS: '',
  GPCR_EXP_ALLOC_TS: '',
  DSN_MAP_FLAG: '',
  SINGLE_EXP_ENABLE: '',
  loading: false,
  backFromSummary: false,
  successFromSummary: false,
  firstSignUp: true,
  expData: null,
  mainExpData: null,
  singleExperienceOnly: false,
};

const state = () => INITIAL_STATE;

const mutations = {
  setSignupState(state, newSignups) {
    const signups = { ...state.signups };
    state.signupArray = newSignups;

    const invitedMcrSignups = {};
    newSignups.forEach(signup => {
      if (signup.type !== 'MCR') signups[signup.type] = signup;
      else {
        invitedMcrSignups[signup.scheduleId] = signup;
      }
    });
    state.invitedMcrSignups = invitedMcrSignups;
    state.signups = signups;
  },
  setSignupDates(state, payload) {
    state[payload.name] = payload.value;
  },
  setLoading(state, isLoading) {
    state.loading = isLoading;
  },
  removeSignupState(state, type) {
    const signups = { ...state.signups };
    delete signups[type];
    state.signups = signups;
  },
  setPendingSignupState(state, pendingSignups) {
    state.pending_signups = pendingSignups;
  },
  setBackFromSummary(state, backFromSummary) {
    state.backFromSummary = backFromSummary;
  },
  setSuccessFromSummary(state, successFromSummary) {
    state.successFromSummary = successFromSummary;
  },
  setFirstSignUpState(state, firstSignUp) {
    state.firstSignUp = firstSignUp;
  },

  setPendingSignupBySignupState(state, data) {
    if (!data.length) return;

    // no need to sort is sorted in db
    const expTypeList = data.map(({ type }) => type);

    const pendingSignupsMap = state.pending_signups.reduce((map, obj) => {
      map[obj.type] = obj;
      return map;
    }, {});

    const finalExpList = [];
    expTypeList.forEach(type => {
      if (type !== 'MCR') {
        finalExpList.push(pendingSignupsMap[type]);
      }
    });
    if (finalExpList.length !== 0) {
      state.firstSignUp = false;
    }
    state.pending_signups = finalExpList;
    const firstSignup = data.find(d => d.type !== 'MCR');
    if (firstSignup) {
      state.singleExperienceOnly = firstSignup.singleExperienceOnly ?? false;
    }
  },
  setExpData(state, expData) {
    state.expData = expData;
  },
  setMainExpData(state, mainExpData) {
    state.mainExpData = mainExpData;
  },
  setSingleExperienceOnly(state, value) {
    state.singleExperienceOnly = value;
  },
};

const actions = {
  async fetchSignup({ dispatch }, conferenceType = 'Global') {
    // Will not call backend api if profile already set
    // if (state.signups !== null) return
    console.log('in fetchSignup');
    await dispatch('forceFetchSignup', conferenceType);
  },
  async forceFetchSignup({ commit, dispatch, rootState }, conferenceType) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(process.env.VUE_APP_API_URL + '/exps/signup', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          conferenceType: conferenceType,
        },
      });
      if (status !== 200) throw new Error('Failed in setting signup status');
      console.log('in forcefetchSignup');

      commit('setSignupState', data);
      commit('setPendingSignupBySignupState', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to load profile.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchDates({ dispatch }) {
    // Uses the shared sysparams cache (fetched at most once per TTL window).
    await dispatch('syncDates', { force: false });
  },

  async forceFetchDates({ dispatch }) {
    // Bypasses the cache and refetches the sysparams payload immediately.
    await dispatch('syncDates', { force: true });
  },

  async syncDates({ commit, dispatch }, { force = false } = {}) {
    try {
      const list = Object.keys(INITIAL_STATE).filter(key => key.includes('_TS'));
      list.push('DSN_MAP_FLAG');
      list.push('SINGLE_EXP_ENABLE');
      // v2: read every date/flag from the single shared sysparams payload.
      const data = await dispatch('sysparams/fetchParams', { force }, { root: true });
      for (const paramName of list) {
        commit('setSignupDates', {
          name: paramName,
          value: data[paramName] ?? null,
        });
      }
    } catch (e) {
      dispatch('toast/error', 'Failed to load profile.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchExpData({ dispatch }, { locale = 'en', conferenceType = 'Global' }) {
    // if (state.expData) return;
    await dispatch('forceFetchExpData', { locale, conferenceType });
  },

  async forceFetchExpData({ commit, rootState }, { locale, conferenceType }) {
    const accessToken = rootState.user.token;
    const { status, data } = await axios.get(process.env.VUE_APP_API_URL + '/cms/experiences/get', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        locale: locale,
        conferenceType: conferenceType,
      },
    });
    if (status !== 200) throw new Error('Failed in fetching exp dates');
    // Changing keys of data to match exp type
    const new_data = {};
    Object.keys(data).forEach(key => {
      const exp_type = data[key].type;
      new_data[exp_type] = data[key];
    });
    commit('setExpData', new_data);
  },

  async fetchMainExpData(
    { commit, dispatch, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(process.env.VUE_APP_API_URL + '/cms/mainExp/get', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          locale: locale,
          conferenceType: conferenceType,
        },
      });
      if (status !== 200) throw new Error('Failed in fetching main exp data');
      commit('setMainExpData', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch main exp data.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async setSignupState(context, payload) {
    await context.commit('setSignupState', payload);
  },
  async removeSignupState(context, type) {
    await context.commit('removeSignupState', type);
  },
  async setPendingSignupState(context, pendingSignups) {
    await context.commit('setPendingSignupState', pendingSignups);
  },
  async setBackFromSummary(context, backFromSummary) {
    await context.commit('setBackFromSummary', backFromSummary);
  },
  async setSuccessFromSummary(context, successFromSummary) {
    await context.commit('setSuccessFromSummary', successFromSummary);
  },
  async setFirstSignUpState(context, firstSignUp) {
    await context.commit('setFirstSignUpState', firstSignUp);
  },
  async setSingleExperienceOnly(context, value) {
    await context.commit('setSingleExperienceOnly', value);
  },
};

const getters = {
  cg(state) {
    if (!state.signups || !('CG' in state.signups)) return null;
    return state.signups.CG;
  },
  spe(state) {
    if (!state.signups || !('SPE' in state.signups)) return null;
    return state.signups.SPE;
  },
  md(state) {
    if (!state.signups || !('MD' in state.signups)) return null;
    return state.signups.MD;
  },
  SIGN_UP_CLOSE_TS(state) {
    return state.SIGN_UP_CLOSE_TS;
  },
  EXP_EVENT_END_TS(state) {
    return state.EXP_EVENT_END_TS;
  },
  EXP_ALLOC_TS(state) {
    return state.EXP_ALLOC_TS;
  },
  GPCR_EXP_ALLOC_TS(state) {
    return state.GPCR_EXP_ALLOC_TS;
  },
  DSN_MAP_FLAG(state) {
    return state.DSN_MAP_FLAG;
  },
  SINGLE_EXP_ENABLE(state) {
    return state.SINGLE_EXP_ENABLE;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
